import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { KccButton } from 'lib/components/KccButton/KccButton';

export const Wrapper = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    flex-direction: column;
    gap: ${kcc.spacing.LG};
    width: 100%;
    height: 100%;
    background-color: ${kcc.colors.WHITE};
  `}
`;

export const Header = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    flex-direction: column;
    gap: ${kcc.spacing.XS};
  `}
`;

export const ScheduleTitle = styled.h2`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    margin: 0;
    font-size: ${kcc.typography.FONT_SIZE.HEADING_4}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    line-height: ${kcc.typography.LINE_HEIGHT.HEADING_3};
    color: ${kcc.colors.TEXT};
  `}
`;

export const TotalProgress = styled.p`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    margin: 0;
    font-size: ${kcc.typography.FONT_SIZE.BASE}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.REGULAR};
    line-height: ${kcc.typography.LINE_HEIGHT.SM};
    color: ${kcc.colors.TEXT};
  `}
`;

export const ScrollArea = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

export const TimelineList = styled.div`
  ${(): FlattenSimpleInterpolation => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
  `}
`;

const fadeStyles = css<{ $isVisible: boolean }>`
  ${({ theme: { kcc }, $isVisible }): FlattenSimpleInterpolation => css`
    position: absolute;
    left: 0;
    right: 0;
    height: ${kcc.size.S700}px;
    pointer-events: none;
    z-index: 1;
    opacity: ${$isVisible ? 1 : 0};
    transition: opacity 0.2s ease;
  `}
`;

export const FadeTop = styled.div<{ $isVisible: boolean }>`
  ${fadeStyles}
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    top: 0;
    background: linear-gradient(to bottom, ${kcc.colors.WHITE}, transparent);
  `}
`;

export const FadeBottom = styled.div<{ $isVisible: boolean }>`
  ${fadeStyles}
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    bottom: 0;
    background: linear-gradient(to top, ${kcc.colors.WHITE}, transparent);
  `}
`;

export const TimelineItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme: { kcc } }): string => kcc.spacing.SM};
  width: 100%;
`;

export const TimelineRail = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    gap: ${kcc.spacing.XS};
    padding-bottom: ${kcc.spacing.XS};
    flex-shrink: 0;
  `}
`;

export type TimelineTone = 'success' | 'warning' | 'upcoming' | 'cleaning' | 'muted';

export const StatusIcon = styled.div<{ $tone: TimelineTone }>`
  ${({ theme: { kcc }, $tone }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${kcc.size.S500}px;
    height: ${kcc.size.S500}px;
    border-radius: ${kcc.borderRadius.PILL}px;
    flex-shrink: 0;

    ${$tone === 'success' &&
    css`
      background-color: ${kcc.colors.SUCCESS};
      color: ${kcc.colors.WHITE};
    `}

    ${$tone === 'warning' &&
    css`
      background-color: ${kcc.colors.WARNING};
      color: ${kcc.colors.WHITE};

      /* warningNew uses a hardcoded fill; force white on the warning pill */
      svg path {
        fill: ${kcc.colors.WHITE};
      }
    `}

    ${$tone === 'upcoming' &&
    css`
      background-color: transparent;
      border: ${kcc.lineWidth.BORDER}px solid ${kcc.colors.BORDER_SECONDARY};
      color: ${kcc.colors.TEXT};
    `}

    ${$tone === 'muted' &&
    css`
      background-color: transparent;
      border: ${kcc.lineWidth.BORDER}px solid ${kcc.colors.BORDER_SECONDARY};
      color: ${kcc.colors.TEXT_SECONDARY};

      svg path {
        fill: currentColor;
      }
    `}
  `}
`;

export const TimelineLine = styled.div<{ $tone: TimelineTone }>`
  ${({ theme: { kcc }, $tone }): FlattenSimpleInterpolation => css`
    flex: 1;
    width: 2px;
    min-height: ${kcc.size.S100}px;
    background-color: ${{
      success: kcc.colors.SUCCESS,
      warning: kcc.colors.WARNING,
      upcoming: kcc.colors.BORDER_SECONDARY,
      cleaning: kcc.colors.INFO,
      muted: kcc.colors.BORDER_SECONDARY,
    }[$tone]};
  `}
`;

export const ItemContent = styled.div<{ $isLast?: boolean }>`
  ${({ theme: { kcc }, $isLast }): FlattenSimpleInterpolation => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    min-width: 0;
    padding-top: ${kcc.spacing.XS};
    padding-bottom: ${$isLast ? 0 : kcc.spacing.LG};
  `}
`;

export const RouteTitle = styled.p`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    margin: 0;
    font-size: ${kcc.typography.FONT_SIZE.LG}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    line-height: ${kcc.typography.LINE_HEIGHT.BASE};
    color: ${kcc.colors.TEXT};
  `}
`;

export const MetricsRow = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${kcc.spacing.XS};
  `}
`;

export const SecondaryText = styled.span`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    font-size: ${kcc.typography.FONT_SIZE.BASE}px;
    line-height: ${kcc.typography.LINE_HEIGHT.SM};
    color: ${kcc.colors.TEXT};
  `}
`;

export const ReportLink = styled.button`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: inline-flex;
    align-items: center;
    gap: ${kcc.spacing.XS};
    margin-top: ${kcc.spacing.XS};
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: ${kcc.typography.FONT_SIZE.BASE}px;
    line-height: ${kcc.typography.LINE_HEIGHT.SM};
    color: ${kcc.colors.TEXT};
    text-decoration: underline;

    .svg-icon.download svg path {
      fill: currentColor;
    }
  `}
`;

export const CleaningMeta = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    gap: ${kcc.spacing.SM};
    flex-wrap: wrap;
  `}
`;

export const SkipRouteButton = styled(KccButton).attrs({
  type: 'primary',
})`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    margin-top: ${kcc.spacing.XS};

    .ant-btn-icon svg path {
      fill: ${kcc.colors.PRIMARY_KARCHER};
    }

    /* A disabled button greys its label, so the icon follows it instead of staying yellow. */
    &:disabled .ant-btn-icon svg path,
    &.ant-btn-disabled .ant-btn-icon svg path {
      fill: currentColor;
    }

    /* The pending spinner is an open stroked arc, so the fills above must not reach it. Last, so it wins over them. */
    .ant-btn-icon [role='progressbar'] svg path {
      fill: none;
    }
  `}
`;

export const ExpectedAvailability = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${kcc.spacing.XXS};
    width: 100%;
    padding-top: ${kcc.size.S300 + 4}px;
  `}
`;

export const ExpectedTitle = styled.p`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    margin: 0;
    width: 100%;
    text-align: center;
    font-size: ${kcc.typography.FONT_SIZE.SM}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.REGULAR};
    line-height: ${kcc.typography.LINE_HEIGHT.BASE};
    color: ${kcc.colors.TEXT};
  `}
`;

export const ExpectedSegments = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    justify-content: center;
    gap: ${kcc.spacing.LG};
    width: 100%;
  `}
`;

export const ExpectedSegment = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${kcc.spacing.XXS};
  `}
`;

export const ExpectedValueRow = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${kcc.spacing.XXS};
  `}
`;

export const ExpectedIcon = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    width: ${kcc.size.S300}px;
    height: ${kcc.size.S300}px;
  `}
`;

export const ExpectedValue = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    gap: ${kcc.spacing.XS};
    font-size: ${kcc.typography.FONT_SIZE.LG}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    line-height: ${kcc.typography.LINE_HEIGHT.BASE};
    color: ${kcc.colors.TEXT};
    white-space: nowrap;
  `}
`;

export const ExpectedUnitGroup = styled.span`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    gap: ${kcc.spacing.XXS};
  `}
`;

export const ExpectedDescription = styled.p`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    margin: 0;
    width: 100%;
    text-align: center;
    font-size: ${kcc.typography.FONT_SIZE.SM}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.REGULAR};
    line-height: ${kcc.typography.LINE_HEIGHT.BASE};
    color: ${kcc.colors.TEXT_SECONDARY};
  `}
`;
